import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { gsap, MOTION_ANY, MOTION_DESKTOP } from '../lib/gsap';

const MILESTONES = ['2016', '2019', '2020', '2022', '2023', '2024', '2025', '2026'];
const TODAY_KEYS = ['platform', 'aggregation', 'localization', 'payments', 'campaigns', 'api'];

const Milestone = ({ year, i, t, onActive }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.6 });
  useEffect(() => { if (inView) onActive(i); }, [inView, i, onActive]);
  return (
    <li ref={ref} className="relative pl-10 lg:pl-14 py-10 lg:py-14 border-b border-[#F4F0E8]/[0.07] last:border-b-0" data-testid={`milestone-${year}`}>
      <span className={`absolute left-[-4.5px] top-[3.35rem] lg:top-[4.1rem] w-2.5 h-2.5 rotate-45 border transition-colors duration-500 ${inView ? 'bg-[#C8AA73] border-[#C8AA73]' : 'bg-[#080808] border-[#F4F0E8]/25'}`} />
      <span className="font-mono-lux text-sm text-[#C8AA73]">{year}</span>
      <h4 className="font-display text-2xl sm:text-3xl lg:text-[2.25rem] leading-tight text-[#F4F0E8] mt-3">
        {t(`about.milestones.year${year}.title`)}
      </h4>
      <p className="mt-4 text-base leading-relaxed text-[#ABA59A] max-w-[560px]">{t(`about.milestones.year${year}.desc`)}</p>
    </li>
  );
};

const Statement = ({ text }) => (
  <p className="about-statement font-display font-light text-[clamp(1.6rem,3.1vw,3rem)] leading-[1.22] tracking-[-0.015em] text-[#F4F0E8]">
    {text.split(' ').map((w, i) => (
      <span key={i} className="about-word inline-block mr-[0.26em]">{w}</span>
    ))}
  </p>
);

export const AboutSection = () => {
  const { t, language } = useLanguage();
  const root = useRef(null);
  const [activeYear, setActiveYear] = useState(0);

  useEffect(() => {
    const mm = gsap.matchMedia();
    mm.add(MOTION_ANY, () => {
      const ctx = gsap.context(() => {
        gsap.fromTo('.about-word', { opacity: 0.18 }, {
          opacity: 1, stagger: 0.05, ease: 'none',
          scrollTrigger: { trigger: '.about-statement', start: 'top 80%', end: 'bottom 45%', scrub: true },
        });
        gsap.fromTo('.journey-fill', { scaleY: 0 }, {
          scaleY: 1, ease: 'none',
          scrollTrigger: { trigger: '.journey-list', start: 'top 60%', end: 'bottom 60%', scrub: true },
        });
      }, root);
      return () => ctx.revert();
    });
    mm.add(MOTION_DESKTOP, () => {
      const ctx = gsap.context(() => {
        gsap.fromTo('.today-panel', { y: 140, scale: 0.95 }, {
          y: 0, scale: 1, ease: 'none',
          scrollTrigger: { trigger: '.today-panel', start: 'top bottom', end: 'top 30%', scrub: true },
        });
      }, root);
      return () => ctx.revert();
    });
    return () => mm.revert();
  }, [language]);

  return (
    <section id="about" ref={root} className="relative z-10 bg-[#080808] px-6 lg:px-12 pt-28 lg:pt-44 pb-28 lg:pb-40" data-testid="about-section">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow">{t('about.label')}</p>
          </div>
          <div className="lg:col-span-8">
            <h2 className="display-xl text-[clamp(2.6rem,5.6vw,5.4rem)]" data-testid="about-title">{t('about.title')}</h2>
            <div className="mt-14" key={language}>
              <Statement text={t('about.description1')} />
            </div>
            <p className="mt-12 lg:ml-[33%] max-w-[560px] text-base sm:text-lg leading-relaxed text-[#ABA59A]">{t('about.description2')}</p>
          </div>
        </div>

        <div className="mt-32 lg:mt-48 grid lg:grid-cols-12 gap-10 lg:gap-16" data-testid="about-journey">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <h3 className="font-display text-3xl sm:text-4xl text-[#F4F0E8]">{t('about.ourJourney')}</h3>
              <div className="hidden lg:block mt-10 overflow-hidden h-[clamp(7rem,13vw,12.5rem)]" aria-hidden="true">
                <div className="transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]" style={{ transform: `translateY(-${activeYear * 100}%)`, height: '100%' }}>
                  {MILESTONES.map((y) => (
                    <div key={y} className="h-full display-xl text-[clamp(7rem,13vw,12.5rem)] leading-none metal-text">{y}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <span className="absolute left-0 top-0 bottom-0 w-px bg-[#F4F0E8]/10" />
            <span className="journey-fill absolute left-0 top-0 bottom-0 w-px bg-[#C8AA73] origin-top" />
            <ol className="journey-list">
              {MILESTONES.map((y, i) => (
                <Milestone key={y} year={y} i={i} t={t} onActive={setActiveYear} />
              ))}
            </ol>
          </div>
        </div>

        <div className="today-panel relative mt-28 lg:mt-40 rounded-[28px] lg:rounded-[36px] metal-edge bg-[#121212] px-6 py-14 sm:px-12 lg:px-20 lg:py-24 overflow-hidden" data-testid="about-today">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 80% at 100% 0%, rgba(200,170,115,0.08), transparent 60%)' }} />
          <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <h3 className="display-xl text-[clamp(2.4rem,4.6vw,4.4rem)]">{t('about.today')}</h3>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#ABA59A] max-w-[440px]">{t('about.todayDesc')}</p>
            </div>
            <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-x-10">
              {TODAY_KEYS.map((k, i) => (
                <li key={k} className="flex gap-5 py-6 border-t border-[#F4F0E8]/[0.08]" data-testid={`about-today-${k}`}>
                  <span className="font-mono-lux text-[0.7rem] text-[#C8AA73] pt-1.5">0{i + 1}</span>
                  <span className="text-[#F4F0E8] text-base sm:text-lg leading-snug">{t(`about.services.${k}`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="relative mt-12 pt-10 border-t border-[#C8AA73]/20 text-[#ABA59A] text-base leading-relaxed max-w-[760px] lg:ml-[41.66%]">{t('about.todayFooter')}</p>
        </div>

        <div className="mt-32 lg:mt-48 grid lg:grid-cols-12 gap-10 lg:gap-16" data-testid="about-vision">
          <div className="lg:col-span-4">
            <p className="eyebrow">{t('about.ourVision')}</p>
          </div>
          <blockquote className="lg:col-span-8 font-display font-light italic text-[clamp(1.8rem,3.6vw,3.5rem)] leading-[1.18] text-[#F4F0E8]">
            <span className="text-[#C8AA73] not-italic">“</span>{t('about.visionDesc')}<span className="text-[#C8AA73] not-italic">”</span>
          </blockquote>
        </div>
      </div>
    </section>
  );
};
